<script>
	import { Canvas, LayerCake, Svg, Html } from "layercake";
	import { tweened } from "svelte/motion";
	import { cubicInOut, cubicOut } from "svelte/easing";
	import { geoOrthographic } from "d3-geo";

	import MapCanvas from "./components/Map.canvas.svelte";
	import Points from "./components/Points.svelte";
	import AxisX from "../common/AxisX.svelte";
	import AxisSectionHighlight from "./components/AxisSectionHighlight.svelte";
	import Arcs from "./components/Arcs.svelte";
	import Legend from "../common/Legend.svelte";
	import MapLabels from "./components/MapLabels.svelte";

	import {
		worldFeatures,
		statesFeatures,
		geojsonFitEurope,
		geojsonFitGermany,
		geojsonFitEUManufacturers,
		geojsonFitUS,
		rotateXEurope,
		rotateYEurope,
		rotateXUS,
		rotateYUS,
	} from "../../data";
	import { scaleBand, scaleLinear } from "d3-scale";
	import { colors } from "../../config";

	export let data = {
		type: "FeatureCollection",
		features: [],
	};
	export let timeline;
	export let progress;
	export let component;
	// export let mapRegions;
	// export let mapBorders
	// export let currYear = 2023;
	export let map;
	export let layout;
	export let highlight;
	export let annotations = [];
	export let showArcs = false;
	export let legendItems;

	export let greyedOut = false;
	export let visible = (props) => true;
	export let radius;
	export let x;
	export let onlyPoints;
	export let noPositionTweening;

	$: annotated = (props) => annotations?.find(({ name }) => name === props.case)

	$: minRadius = containerWidth < 450 ? 1 : 4;
	$: maxRadius = containerWidth < 450 ? 12 : 20;
	$: radius = radius || (() => 1);
	$: x = x || ((props) => props.year);
	$: xTicks = onlyPoints ? [1985, 1990, 1995, 2000, 2005, 2010, 2015, 2020] : [2000, 2005, 2010, 2015, 2020]
	$: xDomain =  onlyPoints ? undefined : [2000, 2023]
	// ///////////////////////////////////////////////////////////
	// DETERMINE DATA TO BE SHOWN
	// ///////////////////////////////////////////////////////////
	$: minRadiusValue = Math.min(...data.features.map((d) => radius(d.properties)));
	$: maxRadiusValue = Math.max(...data.features.map((d) => radius(d.properties)));

	// ///////////////////////////////////////////////////////////
	// TIME ANIMATION
	// ///////////////////////////////////////////////////////////
	const startYear = Math.min(...data.features.map((f) => f.properties.year));
	const endYear = 2023;

	const currYear = tweened(timeline ? startYear : endYear, { duration: 4000, easing: cubicOut });
	if (timeline) setTimeout(() => ($currYear = endYear), 200);

	// // ///////////////////////////////////////////////////////////
	// // CALCULATE PROJECTIONS FOR EUROPE AND THE US
	// // ///////////////////////////////////////////////////////////
	let containerWidth, containerHeight;

	// calculate the projections for the US and Europe depending on the width and height of the container
	$: projectionUS = geoOrthographic()
		.rotate([rotateXUS, rotateYUS])
		.fitSize([containerWidth - 30, containerHeight - 30], geojsonFitUS);

	$: projectionEurope = geoOrthographic()
		.rotate([rotateXEurope, rotateYEurope])
		.fitSize([containerWidth, containerHeight - 30], geojsonFitEurope);

	$: projectionEUManufacturers = geoOrthographic()
		.rotate([rotateXEurope, rotateYEurope])
		.fitSize([containerWidth, containerHeight - 30], geojsonFitEUManufacturers);

	$: projectionGermany = geoOrthographic()
		.rotate([rotateXEurope, rotateYEurope])
		.fitSize([containerWidth, containerHeight - 30], geojsonFitGermany);

	$: projections = {
		europe: projectionEUManufacturers,
		eu_manufacturers: projectionEUManufacturers,
		us: projectionUS,
		germany: projectionGermany,
	};

	// ///////////////////////////////////////////////////////////
	// TRANSITION BETWEEN THE TWO PROJECTIONS
	// ///////////////////////////////////////////////////////////

	const transitionParameters = {
		duration: 3000,
		easing: cubicInOut,
	};

	// tween parameters for rotation, translation and scale for animation
	const rotateX = tweened(rotateXUS, transitionParameters);
	const rotateY = tweened(rotateYUS, transitionParameters);
	$: translateX = tweened(projectionUS.translate()[0], transitionParameters);
	$: translateY = tweened(projectionUS.translate()[1], transitionParameters);
	$: scale = tweened(projectionUS.scale(), transitionParameters);

	// update the projection based on the tweened values
	$: projection = geoOrthographic().rotate([$rotateX, $rotateY]).scale($scale).translate([$translateX, $translateY]);

	let lastMap;
	$: if (map !== "none") {
		const target = projections[map];
		$rotateX = target.rotate()[0];
		$rotateY = target.rotate()[1];

		$translateX = target.translate()[0];
		$translateY = target.translate()[1];

		const transAtlantic =
			(lastMap === "us" && ["europe", "eu_manufacturers", "germany"].includes(map)) ||
			(["europe", "eu_manufacturers", "germany"].includes(lastMap) && map === "us");

		if (lastMap && transAtlantic) {
			// simulate zooming out and then zooming back in to target
			const scaleDelay = 900;
			$scale = 0.3 * target.scale();
			setTimeout(() => ($scale = target.scale()), scaleDelay);
		} else {
			$scale = target.scale();
		}
		lastMap = map;
	}
</script>

<LayerCake
	padding={{ top: 30, bottom: 0, left: 15, right: 15 }}
	xScale={layout === "none" ? scaleBand() : scaleLinear()}
	{x}
	{xDomain}
	r={radius}
	rDomain={[minRadiusValue, maxRadiusValue]}
	rRange={[minRadius, maxRadius]}
	flatData={data.features.map((f) => f.properties)}
	{data}
	bind:containerWidth
	bind:containerHeight
>
	{#if legendItems}
		<Legend
			items={legendItems}
			paddingTop={layout === 'beeswarm' ? 70 : (component === 'manufacturers' ? 30 : (component === 'shootings' && layout === 'map' ? 45 : 60))}
		/>
	{/if}
	{#if map !== "none"}
		<Canvas>
			<MapCanvas
				{statesFeatures}
				{worldFeatures}
				{projection}
				hideWorldFeatures={map !== "europe" && $scale === projectionUS.scale()}
				fill={colors.mapRegions}
				stroke={colors.mapBorders}
				background={colors.background}
			/>
		</Canvas>
	{/if}
	<Svg>
		{#if layout !== "map"}
			<AxisX
				tickPosition={"top"}
				baseline={false}
				baselineWithPadding={true}
				ticks={xTicks}
			/>
		{/if}
		{#if (layout === "histogram" || layout === 'beeswarm') && onlyPoints}
			<AxisSectionHighlight  />
		{/if}
		<Points
			{layout}
			{projection}
			color={component === 'manufacturers' ? colors.pointsManufacturers : colors.points}
			highlightColor={colors.pointsHighlighted}
			showHighlights={highlight}
			sizedUpOnEntry={timeline}
			highlighted={highlight ? highlight : () => false}
			{annotated}
			visible={(props) => (!timeline || $currYear > props.year) && (!visible || visible(props))}
			radiusFactor={layout === "beeswarm" ? 1.5 : 1}
			{greyedOut}
			{noPositionTweening}
		/>
		{#if showArcs}
			<Arcs {projection} color={colors.arcs} />
		{/if}
		<MapLabels 
			{projection}
			color={colors.pointsHighlighted}
		/>
	</Svg>
</LayerCake>

<style>
</style>
